import { action, computed, makeObservable, observable, reaction } from "mobx";
import { WorkflowReviewStore } from "./WorkflowReviewStore";
import { BaseStore } from "stores/BaseStore";
import { WorkflowMetadata } from "services/EvaluationService";
import { LMType } from "models/LicensedModule";
import { WorkflowConditionType } from "components/Admin/Organizations/OrganizationDetail/OrganizationWorkflows/Types/WorkflowModels";
import {
    AptWorkflowUpdatePayload,
    EvalutationDisputeWorkflowUpdatePayload,
    ScoreEscalationWorkflowUpdatePayload,
    WorkflowUpdateInstanceLevelPayload,
} from "services/WorkflowService";
import { WorkflowInfoAssignedWorkflowInstance } from "../types";
import EvaluationModule from "models/EvaluationModule";
import RootStore from "stores/RootStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { QuestionDependencyQuestionAnswerCondition } from "models/Question";

export const UpdateWorkflow = "Update Workflow";

export class WorkflowStore extends BaseStore {
    static RejectResult: "reject" = "reject";
    static ApproveResult: "approve" = "approve";
    static CompletedResult: "complete" = "complete";

    readonly workflowReviewStore: WorkflowReviewStore;

    @observable private modulesNameById: Map<string, string> = new Map<
        string,
        string
    >();

    @observable.ref workflowMetadata: Record<
        string /*keyed by instanceId*/,
        WorkflowMetadata[]
    > = {};

    constructor(workflowReviewStore: WorkflowReviewStore) {
        super("Eval Workflow");
        makeObservable(this);
        this.workflowReviewStore = workflowReviewStore;

        reaction(
            (r) => this.workflowReviewStore.evalLicensedModules,
            (arg) => {
                arg?.filter(
                    (value) => value.lmType !== LMType.Workflow,
                ).forEach((value) => {
                    this.modulesNameById.set(value.id, value.name!);
                });
            },
        );
    }

    async updateAptWorkflow(payload: AptWorkflowUpdatePayload) {
        await this.setupAsyncTask(UpdateWorkflow, async () => {
            try {
                await this.workflowReviewStore.workflowService.updateAptWorkflow(
                    payload,
                );
            } catch (e) {
                RootStore()
                    .getStore(MessageStore)
                    .logError(
                        e instanceof Error
                            ? e.message
                            : "Error updating workflow",
                    );
            }
        });
    }

    async updateScoreEscalationWorkflow(
        payload: ScoreEscalationWorkflowUpdatePayload,
    ) {
        await this.setupAsyncTask(UpdateWorkflow, async () => {
            try {
                await this.workflowReviewStore.workflowService.updateScoreEscalationWorkflow(
                    payload,
                );
            } catch (e) {
                RootStore()
                    .getStore(MessageStore)
                    .logError(
                        e instanceof Error
                            ? e.message
                            : "Error updating workflow",
                    );
            }
        });
    }

    async updateEvaluationDisputeWorkflow(
        payload: EvalutationDisputeWorkflowUpdatePayload,
    ) {
        await this.setupAsyncTask(UpdateWorkflow, async () => {
            try {
                await this.workflowReviewStore.workflowService.updateEvaluationDisputeWorkflow(
                    payload,
                );
            } catch (e) {
                RootStore()
                    .getStore(MessageStore)
                    .logError(
                        e instanceof Error
                            ? e.message
                            : "Error updating workflow",
                    );
            }
        });
    }

    @action
    onCompleteDisputeLevel = async (
        instanceId: string,
        workflowModuleId: string,
        level: number,
        note: string,
        result: "approve" | "reject" | "complete" | null,
        moduleScores: {
            module: EvaluationModule;
            score: number;
        }[],
    ) => {
        const instance = this.workflowReviewStore.workflowReviewerInfo;

        let foundLevel = instance?.workflowInfo.workflowLevelInfos?.find(
            (_, idx) => idx === level,
        );
        if (instance && foundLevel) {
            let activeLevel: WorkflowUpdateInstanceLevelPayload = {
                workflowInstanceId: instance.workflowInfo.instanceId,
                result,
                note: note ?? "",
                workflowMessagingMetadataId:
                    foundLevel.workflowMessagingMetadataId,
                userId: this.workflowReviewStore.authStore._user.profile.sub,
                evaluationModuleId: foundLevel.evaluationModule?.id ?? "",
                evaluationModule: foundLevel.evaluationModule,
                conversationModule: null,
                createdBy:
                    this.workflowReviewStore.authStore._user.profile.email,
            };

            this.setupAsyncTask(UpdateWorkflow, async () => {
                try {
                    const answers = [
                        ...instance.workflowInfo.answers,
                        ...(instance.workflowInfo.workflowLevelInfos[level]
                            .answers ?? []),
                    ];

                    answers.forEach((answer) => {
                        answer.answerDisputes = undefined;
                    });

                    const payload: EvalutationDisputeWorkflowUpdatePayload = {
                        evaluationId: this.workflowReviewStore.evalId ?? "",
                        workflowInstanceId:
                            instance.workflowInfo.workflowInstanceId,
                        workflowInstanceLevel: activeLevel,
                        answers,
                        disputeEvaluationModuleScores: moduleScores.map(
                            ({ module, score }) => ({
                                disputeEvaluationModuleId: module.id,
                                disputeEvaluationModuleScore: score,
                            }),
                        ),
                    };
                    await this.updateEvaluationDisputeWorkflow(payload);

                    if (this.workflowReviewStore.workflowId) {
                        await this.workflowReviewStore.getWorkflowsForReviewer(
                            this.workflowReviewStore.workflowId,
                        );
                    }

                    //need to get modules after getting info
                    await this.workflowReviewStore.getAllModules();
                } catch {
                    RootStore()
                        .getStore(MessageStore)
                        .logError("Error updating workflow");
                }
            });
        }
    };

    @action
    onCompleteLevel = async (
        instanceId: string,
        workflowModuleId: string,
        level: number,
        note: string,
        result: "approve" | "reject" | "complete" | null,
        moduleScore?: number,
    ) => {
        const instance = this.workflowReviewStore.workflowReviewerInfo;

        const updatedAnswers = instance?.workflowInfo.answers ?? [];

        // Remove tags from answers that are not active
        for (let i = 0; i < updatedAnswers.length; ++i) {
            const answer = updatedAnswers[i];
            const dependencies = answer.question.dependencies;
            if (dependencies) {
                const dependsOnList = dependencies.conditions.map((cond) => ({
                    dependsOnQuestionId: (
                        cond as QuestionDependencyQuestionAnswerCondition
                    ).dependsOnQuestionId,
                    value: (cond as QuestionDependencyQuestionAnswerCondition)
                        .value,
                }));

                let isActive = false;
                for (let j = 0; j < dependsOnList.length; ++j) {
                    const dependsOn = dependsOnList[j];
                    const answerForDependency = updatedAnswers.find(
                        (answer) =>
                            answer.question.id ===
                            dependsOn.dependsOnQuestionId,
                    );

                    // If parent dependency is not in the update list, we can be sure the parent dependency hasn't changed
                    // and this child answer is still active
                    if (answerForDependency === undefined) {
                        isActive = true;
                        break;
                    }

                    const answerValue = answerForDependency?.answerTags.find(
                        (ansTag) => ansTag.isActive,
                    )?.tag?.value;

                    if (answerValue === dependsOn.value) {
                        isActive = true;
                        break;
                    }
                }

                if (!isActive) {
                    answer.setAnswerTags([]);
                }
            }
        }

        let foundLevel = instance?.workflowInfo.workflowLevelInfos?.find(
            (_, idx) => idx === level,
        );
        if (instance && foundLevel) {
            let activeLevel: WorkflowUpdateInstanceLevelPayload = {
                workflowInstanceId: instance.workflowInfo.instanceId,
                result,
                note: note ?? "",
                workflowMessagingMetadataId:
                    foundLevel.workflowMessagingMetadataId,
                userId: this.workflowReviewStore.authStore._user.profile.sub,
                evaluationModuleId: workflowModuleId,
                evaluationModule: foundLevel.evaluationModule,
                conversationModule: null,
                createdBy:
                    this.workflowReviewStore.authStore._user.profile.email,
            };

            this.setupAsyncTask(UpdateWorkflow, async () => {
                try {
                    const answers = [
                        ...updatedAnswers,
                        ...(instance.workflowInfo.workflowLevelInfos[level]
                            .answers ?? []),
                    ];
                    switch (
                        this.workflowReviewStore.workflowReviewerInfo
                            ?.workflowType
                    ) {
                        case WorkflowConditionType.APT: {
                            const payload: AptWorkflowUpdatePayload = {
                                evaluationId:
                                    instance.workflowInfo.evaluationModule
                                        ?.evaluationId ?? "",
                                workflowInstanceId:
                                    instance.workflowInfo.workflowInstanceId,
                                workflowInstanceLevel: activeLevel,
                                answers,
                                aptModuleId:
                                    instance.workflowInfo.evaluationModule
                                        ?.id ?? "",
                                aptModuleScore: moduleScore,
                            };
                            await this.updateAptWorkflow(payload);
                            break;
                        }
                        case WorkflowConditionType.ScoreEscalation: {
                            if (
                                moduleScore === null ||
                                moduleScore === undefined
                            ) {
                                this.workflowReviewStore.messageStore.logError(
                                    "Module score not found",
                                );
                                return;
                            }
                            const payload: ScoreEscalationWorkflowUpdatePayload =
                                {
                                    evaluationId:
                                        instance.workflowInfo
                                            .scoreEscalationModule
                                            ?.evaluationId ?? "",
                                    workflowInstanceId:
                                        instance.workflowInfo
                                            .workflowInstanceId,
                                    workflowInstanceLevel: activeLevel,
                                    answers,
                                    scoreEscalationModuleId:
                                        instance.workflowInfo
                                            .scoreEscalationModule.id,
                                    scoreEscalationModuleScore:
                                        moduleScore ?? 0,
                                };
                            await this.updateScoreEscalationWorkflow(payload);
                            break;
                        }
                    }

                    if (this.workflowReviewStore.workflowId) {
                        await this.workflowReviewStore.getWorkflowsForReviewer(
                            this.workflowReviewStore.workflowId,
                        );
                    }

                    //need to get modules after getting info
                    await this.workflowReviewStore.getAllModules();
                } catch {
                    RootStore()
                        .getStore(MessageStore)
                        .logError("Error updating workflow");
                }
            });
        }
    };

    @computed
    get disputeBossLevelExists() {
        if (!this.workflowReviewStore.evalId) {
            return false;
        }

        const disputeInstances = this.listDisputeInstances();

        if (!disputeInstances.length) {
            return false;
        }
        return this.disputeConditionAtBossLevel(disputeInstances);
    }

    disputeConditionAtBossLevel(
        disputeInstances:
            | WorkflowInfoAssignedWorkflowInstance
            | WorkflowInfoAssignedWorkflowInstance[],
    ): any {
        if (!Array.isArray(disputeInstances)) {
            disputeInstances = [disputeInstances];
        }

        const anyBossLevels = disputeInstances.map((disputeInstance) => {
            const meta =
                this.workflowMetadata[disputeInstance.workflowInfo.instanceId];
            const completedLevels =
                disputeInstance.workflowInfo.workflowLevelInfos?.length ?? 0;
            const totalLevels = meta?.length;

            const possiblyAtBossLevel = completedLevels >= totalLevels - 1;
            if (!possiblyAtBossLevel) {
                return false;
            }

            const finishedEarly =
                disputeInstance.workflowInfo.workflowLevelInfos?.some((wil) => {
                    const isComplete =
                        wil.result === WorkflowStore.CompletedResult;

                    return isComplete && !(completedLevels === totalLevels);
                });

            return !finishedEarly && possiblyAtBossLevel;
        });

        const allTrue = anyBossLevels.every((value) => value);
        const allFalse = anyBossLevels.every((value) => !value);

        if (allTrue) {
            return true;
        } else return !allFalse;
    }

    @computed
    get aptInstanceExists() {
        const instances = this.workflowReviewStore.workflowReviewerInfo;
        if (!instances) {
            return false;
        }

        const metadata =
            this.workflowMetadata[instances.workflowInfo.instanceId];
        // are they all APT, if yes, show the tab
        return !!metadata?.some(
            (value1) => value1.type === WorkflowConditionType.APT,
        );

        // [true,true,true,true] //show
        // [true,true,true,true,....,false] ==> show
        // [false,false,false,false] // dont show

        // show APT tab
        // const allTrue = isApt.every((value) => value);
        // const allFalse = isApt.every((value) => !value);

        // if (allTrue) {
        //     return true;
        // } else return !allFalse;
    }

    instanceTitle(
        instance: WorkflowInfoAssignedWorkflowInstance,
        type: WorkflowConditionType,
    ) {
        switch (type) {
            case WorkflowConditionType.APT:
                return `${this.workflowReviewStore.modulesNameById.get(
                    instance.workflowInfo.questionModuleId,
                )}`;
            case WorkflowConditionType.EvaluationDispute:
                return instance.workflowInfo.conditionName;
            case WorkflowConditionType.ScoreEscalation:
                return `${instance.workflowInfo.conditionName}`;
        }
    }

    instanceSubTitle(
        instance: WorkflowInfoAssignedWorkflowInstance,
        type: WorkflowConditionType,
    ) {
        switch (type) {
            case WorkflowConditionType.APT:
                return instance.workflowInfo.question;
            case WorkflowConditionType.EvaluationDispute:
                break;
            case WorkflowConditionType.ScoreEscalation:
                return `${
                    this.modulesNameById.get(
                        instance.workflowInfo.workflowLevelInfos[0]
                            .evaluationModuleId,
                    ) ?? ""
                }`;
        }
    }

    // level 0,1,2
    // metaLevels.length = 3
    pruneEarlyCompleteDisputeInstances(
        instances: WorkflowInfoAssignedWorkflowInstance[],
    ) {
        return instances.filter((instance) => {
            const metaLevels =
                this.workflowMetadata[instance.workflowInfo.instanceId];
            if (
                instance.workflowInfo.implementationType !==
                WorkflowConditionType.APT
            ) {
                const isCompleteBeforeBoss =
                    instance.workflowInfo.workflowLevelInfos?.some(
                        (level, idx) =>
                            level.result === WorkflowStore.CompletedResult &&
                            metaLevels?.length - 1 > idx,
                    );

                return !isCompleteBeforeBoss;
            }
            return true;
        });
    }

    private listDisputeInstances(): WorkflowInfoAssignedWorkflowInstance[] {
        const instances = this.workflowReviewStore
            .workflowInfoWithAssignedWorkflowInstances
            ? [
                  this.workflowReviewStore
                      .workflowInfoWithAssignedWorkflowInstances,
              ]
            : [];
        return instances.filter((value) => {
            return [
                WorkflowConditionType.EvaluationDispute,
                WorkflowConditionType.ScoreEscalation,
            ].includes(value.workflowInfo.implementationType!);
        });
    }
}
